import React from "react";
import classNames from "classnames";
import ImmutablePropTypes from "react-immutable-proptypes";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import { logoutUser as logoutUserAction, saveUserAction } from "actions";
import { updateOne } from "actions/resources";
import InputPassword from "components/Common/InputPassword";
import CheckboxPanel from "components/Common/CheckboxPanel";
import Button from "components/Common/Button";
import SvgIcon from "components/Common/SvgIcon";
import Select from "components/Common/Select";
import UserImage from "components/Common/UserImage";
import PasswordGuidelines from "components/Common/PasswordGuidelines";
import { User } from "resourceModels";
import { getResource } from "selectors/resources";
import { NO_OP_FUNCTION } from "services/helpers";
import { validatePasswordAlphaNum, validatePasswordSpecialChar } from "services/validations";
import { VALID_PASSWORD_LENGTH } from "constants/passwordGuidelines";

import ModalProfileSecurity from "./ModalProfileSecurity";
import { updatePassword as updatePasswordAction, uploadAvatar as uploadAvatarAction } from "./actions";
import "./style.scss";

const USER_ROLES = [
  {
    label: "Agent",
    value: "agent"
  },
  {
    label: "Admin",
    value: "admin"
  },
  {
    label: "Owner",
    value: "owner"
  }
];

class ModalProfile extends React.Component {
  handlePasswordSubmitBound = this.handlePasswordSubmit.bind(this);
  handleFileUploadBound = this.handleFileUpload.bind(this);
  handleAddPasswordGuidelinesBound = this.handleAddPasswordGuidelines.bind(this);
  updateEmailPreferenceBound = this.updateEmailPreference.bind(this);
  changePasswordBound = this.changePassword.bind(this);
  changeOldPasswordBound = this.changeOldPassword.bind(this);

  state = {
    showPasswordGuidelines: false,
    password: "",
    oldPassword: ""
  };

  /**
   * @param {Object} prevProps
   */
  componentDidUpdate(prevProps) {
    const { modalProfile } = this.props;

    if (prevProps.modalProfile.get("passwordReset") !== modalProfile.get("passwordReset")) {
      this.resetPasswordState();
    }
  }

  resetPasswordState = () => {
    this.setState({
      password: "",
      oldPassword: ""
    });
  }

  /**
   * toggles is password guidelines are visible
   */
  handleAddPasswordGuidelines() {
    this.setState({
      showPasswordGuidelines: true
    });
  }

  /**
   */
  handlePasswordSubmit() {
    const {
      /** @type User */
      user,
      updatePassword
    } = this.props;

    const {
      password,
      oldPassword
    } = this.state;

    if (password && oldPassword && this.isPasswordValid()) {
      updatePassword(oldPassword, password, user.id);
    }
  }

  /**
   * @param {Event} e
   */
  handleFileUpload(e) {
    const { uploadAvatar } = this.props;
    const fileToUpload = e.target.files.item(0);

    if (fileToUpload) {
      uploadAvatar(fileToUpload);
    }
  }

  /**
   * @param {Event} e
   */
  changePassword(e) {
    this.setState({
      password: e.target.value
    });
  }

  /**
   * @param {Event} e
   */
  changeOldPassword(e) {
    this.setState({
      oldPassword: e.target.value
    });
  }

  /**
   */
  updateEmailPreference() {
    const {
      /** @type User */
      user,
      updateUser,
      saveUser
    } = this.props;

    const isEmailNotificationActive = !(
      user.emails.get("requestFailedNotifications") ||
      user.emails.get("variableFailedNotifications") ||
      user.emails.get("scheduledCallbackFailedNotifications")
    );

    // note: any email notification setting that isn't sent here gets erased
    updateUser(user.id, {
      emails: {
        requestFailedNotifications: isEmailNotificationActive,
        variableFailedNotifications: isEmailNotificationActive,
        scheduledCallbackFailedNotifications: isEmailNotificationActive
      }
    });

    saveUser(user.id);
  }

  /**
   * Check if the password in the 'new password' field is valid
   * @returns {boolean}
   */
  isPasswordValid() {
    const { password, oldPassword } = this.state;

    return password.length >= VALID_PASSWORD_LENGTH &&
      password !== oldPassword &&
      validatePasswordAlphaNum(password) &&
      validatePasswordSpecialChar(password);
  }

  /**
   * @returns {ReactElement}
   */
  render() {
    const {
      /** @type User */
      user,
      updateUser,
      saveUser,
      modalProfile,
      logoutUser,
      setState
    } = this.props;
    const {
      password,
      oldPassword,
      showPasswordGuidelines
    } = this.state;

    const avatarLoading = modalProfile.get("avatarUploading");
    const isEmailToggleChecked = (
      user.emails.get("requestFailedNotifications") ||
      user.emails.get("variableFailedNotifications") ||
      user.emails.get("scheduledCallbackFailedNotifications")
    );

    return (
      <div className="ModalProfile Modal__modal">
        <h5 className="Modal__title">Your Profile</h5>
        <div className="Modal__content">
          <section className="Modal__section ModalProfile__details">
            <h5 className="Modal__section__title">Edit Profile</h5>
            <div className="ModalProfile__details__user-info">
              <div className="ModalProfile__details__user-info__inputs">
                <div className="ModalProfile__details__user-info__inputs__input">
                  <label className="g-input__label ModalProfile__label" htmlFor="ModalProfile__name">
                    Full Name
                  </label>
                  <input
                    id="ModalProfile__name"
                    value={user.name}
                    onChange={e => updateUser(user.id, { name: e.target.value })}
                    placeholder="Jane Doe"
                    className={classNames("g-input", {
                      "g-input--invalid": user.invalidFields.includes("name")
                    })}
                  />
                </div>
                <Button
                  onClick={() => saveUser(user.id)}
                  text={`${
                    modalProfile.get("userLoading")
                      ? "Saving..."
                      : "Save"
                  }`}
                  title={`${
                    modalProfile.get("userLoading")
                      ? "Saving..."
                      : "Save"
                  }`}
                  isLoading={modalProfile.get("userLoading")}
                  icon="Cloud"
                  customClassName="ModalProfile__details"
                  disabled={
                    Boolean(user.invalidFields.length) ||
                    modalProfile.get("userLoading") ||
                    !user.isDirty
                  }
                />
              </div>
              <div className="ModalProfile__details__user-info__role">
                <label
                  className="g-input__label ModalProfile__label"
                  htmlFor="ModalProfile__user-info__role"
                >
                  Role
                </label>
                <Select
                  options={USER_ROLES}
                  value={user.role}
                  onChange={NO_OP_FUNCTION}
                  placeholder="Role"
                  disabled
                />
              </div>
              <UserImage
                customClassName={classNames("ModalProfile__details__user-info__image", {
                  "ModalProfile__details__user-info__image--uploading": avatarLoading
                })}
                showDefaultPicture
                user={user}
                height="80px"
                width="80px"
              >
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
                <div
                  className="ModalProfile__details__user-info__image__uploader"
                  onClick={() => this.selectedFile.click()}
                  role="button"
                  tabIndex={0}
                >
                  <input
                    type="file"
                    ref={(selectedFile) => {
                      this.selectedFile = selectedFile;
                    }}
                    style={{ display: "none" }}
                    accept="image/gif, image/jpeg, image/png, image/svg"
                    onChange={this.handleFileUploadBound}
                  />
                  <div className="ModalProfile__details__user-info__image__uploader__text">
                    {avatarLoading ? (
                      "Uploading..."
                    ) : (
                      <SvgIcon
                        icon="Upload"
                        fillColor="currentColor"
                        customClassName="ModalProfile__details__user-info__image__uploader__text__icon"
                      />
                    )}
                  </div>
                </div>
              </UserImage>
            </div>
          </section>
          <section className="Modal__section ModalProfile__details">
            <h5 className="Modal__section__title">Change Password</h5>
            <section className="Modal__section__sub-section">
              <div className="Modal__section__inputs ModalProfile__details__passwords__inputs">
                <div className="Modal__section__inputs ModalProfile__details__passwords__inputs__input">
                  <label
                    className="g-input__label ModalProfile__label"
                    htmlFor="ModalProfile__old-password"
                  >
                    Current Password
                  </label>
                  <InputPassword
                    setState={setState}
                    value={oldPassword}
                    onPasswordChange={this.changeOldPasswordBound}
                    customClassName="ModalProfile__details__passwords__inputs__input"
                    onKeyDown={this.handlePasswordSubmitBound}
                  />
                </div>
                <div className="Modal__section__inputs ModalProfile__details__passwords__inputs__input">
                  <label
                    className="g-input__label ModalProfile__label"
                    htmlFor="ModalProfile__new-password"
                  >
                    New Password
                  </label>
                  <InputPassword
                    setState={setState}
                    value={password}
                    onPasswordChange={this.changePasswordBound}
                    customClassName="ModalProfile__details__passwords__inputs__input"
                    onKeyDown={this.handlePasswordSubmitBound}
                    onFocus={this.handleAddPasswordGuidelinesBound}
                    inputId="new-password-button"
                    autoComplete="new-password"
                  />
                </div>
              </div>
              {showPasswordGuidelines && (
                <PasswordGuidelines
                  customClassName="ModalProfile__details__passwords"
                  password={password}
                  oldPassword={oldPassword}
                />
              )}
              <div
                className="ModalProfile__details__passwords__button-container"
              >
                <Button
                  onClick={this.handlePasswordSubmitBound}
                  text="Change Password"
                  title="Update Password"
                  customClassName="ModalProfile__details__passwords__inputs__button"
                  disabled={
                    !password ||
                    !oldPassword ||
                    !this.isPasswordValid()
                  }
                />
              </div>
            </section>
          </section>
          <section className="Modal__section">
            <ModalProfileSecurity
              user={user}
              setState={setState}
            />
          </section>
          <section>
            <div className="ModalProfile__email-preference">
              <h5 className="Modal__section__title">Email Preferences</h5>
              <CheckboxPanel
                label="Error Emails"
                checked={isEmailToggleChecked}
                handleToggle={this.updateEmailPreferenceBound}
                showTooltip
                tooltipMessage="Errors are generated when a Request Block or Variable fails"
                tooltipPosition={{
                  dimple: "down",
                  top: -75,
                  left: -80
                }}
              >
                Notify me of any errors in our bot’s Answer content by email.
              </CheckboxPanel>
            </div>
          </section>
        </div>
        <div className="Modal__bottom ModalProfile__bottom">
          <Button
            onClick={logoutUser}
            text="Logout"
            title="Logout"
            light
            icon="SignOut"
            customClassName="ModalProfile__bottom__logout"
          />
        </div>
      </div>
    );
  }
}

ModalProfile.propTypes = {
  user: PropTypes.instanceOf(User).isRequired,
  updateUser: PropTypes.func.isRequired,
  saveUser: PropTypes.func.isRequired,
  modalProfile: ImmutablePropTypes.map.isRequired,
  logoutUser: PropTypes.func.isRequired,
  updatePassword: PropTypes.func.isRequired,
  uploadAvatar: PropTypes.func.isRequired,
  setState: PropTypes.func.isRequired
};

/**
 * @param {Object} state
 * @returns {Object}
 */
function mapStateToProps(state) {
  const { modalProfile } = state;

  return {
    modalProfile,
    user: getResource(state, "user")
  };
}

/**
 * @param {Function} dispatch
 * @returns {Object.<Function>}
 */
function mapDispatchToProps(dispatch) {
  return {
    logoutUser: bindActionCreators(logoutUserAction, dispatch),
    updatePassword: bindActionCreators(updatePasswordAction, dispatch),
    uploadAvatar: bindActionCreators(uploadAvatarAction, dispatch),
    updateUser: (id, data) => dispatch(updateOne("user", id, data)),
    saveUser: id => dispatch(saveUserAction(id))
  };
}

const Connector = connect(
  mapStateToProps,
  mapDispatchToProps
)(ModalProfile);

export default Connector;
